import { createSlice, nanoid } from '@reduxjs/toolkit';
import {
  DEFAULT_STAGE_GRID_HEIGHT,
  DEFAULT_STAGE_GRID_WIDTH
} from '../constants/stageDimensions';
import { DEFAULT_MESSAGE_COLOR, getMessageIconPath, normalizeMessageColor } from '../constants/messageBlocks';

// Grid constants must match Stage.js defaults
const GRID_WIDTH = DEFAULT_STAGE_GRID_WIDTH;
const GRID_HEIGHT = DEFAULT_STAGE_GRID_HEIGHT;
const SIM_DEFAULT_GRID_WIDTH = 20;
const SIM_DEFAULT_GRID_HEIGHT = 15;
const DEFAULT_SCRIPTAREA_WIDTH = 1000;
const DEFAULT_SCRIPTAREA_HEIGHT = 600;

// Center indices are integer grid cells (Stage places at (x+0.5, y+0.5) cells)
const CENTER_X = Math.floor(GRID_WIDTH / 2);
const CENTER_Y = Math.floor(GRID_HEIGHT / 2);

const DEFAULT_TTS_LANGUAGE = 'en-US';

const makeDefaultSpeechSettings = () => ({
  lang: DEFAULT_TTS_LANGUAGE,
});

const ensureActorSpeechSettings = (actor) => {
  if (!actor) return actor;
  if (actor.speechSettings) {
    actor.speechSettings = {
      ...makeDefaultSpeechSettings(),
      ...actor.speechSettings,
    };
    return actor;
  }
  actor.speechSettings = makeDefaultSpeechSettings();
  return actor;
};

// Default starter actor
const makeDefaultstem = () => ({
  id: nanoid(),
  name: 'Stembot',
  image: './assets/characters/stembot.svg',
  x: CENTER_X,
  y: CENTER_Y,
  originalPosition: { x: CENTER_X, y: CENTER_Y },
  homePosition: { x: CENTER_X, y: CENTER_Y },
  wrapAnchors: {
    left: { x: 0, y: CENTER_Y },
    right: { x: GRID_WIDTH - 1, y: CENTER_Y },
    top: { x: CENTER_X, y: 0 },
    bottom: { x: CENTER_X, y: GRID_HEIGHT - 1 },
  },
  direction: 0,
  height: 5,
  scripts: [],
  size: 1,
  visible: true,
  speechMessage: null,
  speechSettings: makeDefaultSpeechSettings(),
});

// Default scene
const DEFAULT_DRAW_LINE_SETTINGS = {
  enabled: false,
  thickness: 3,
  size: 10,
  color: '#FF0000',
};

const loadStoredDrawLineSettings = () => {
  if (typeof localStorage === 'undefined') {
    return { ...DEFAULT_DRAW_LINE_SETTINGS };
  }
  try {
    const stored = localStorage.getItem('simulatorDrawLineSettings');
    if (stored) {
      return { ...DEFAULT_DRAW_LINE_SETTINGS, ...JSON.parse(stored) };
    }
  } catch (err) {
    console.warn('Failed to read draw line settings from storage:', err);
  }
  return { ...DEFAULT_DRAW_LINE_SETTINGS };
};

const MAX_ACTOR_TRAIL_POINTS = 500;

const ensureActorTrails = (scene) => {
  if (!scene.actorTrails) {
    scene.actorTrails = {};
  }
  return scene.actorTrails;
};

const appendActorTrailPoint = (scene, actorId, x, y) => {
  if (!scene || actorId == null) return;
  const trails = ensureActorTrails(scene);
  if (!trails[actorId]) {
    trails[actorId] = [];
  }
  const actorTrail = trails[actorId];
  const lastPoint = actorTrail[actorTrail.length - 1];
  if (lastPoint && lastPoint.x === x && lastPoint.y === y) {
    return;
  }
  actorTrail.push({ x, y });
  if (actorTrail.length > MAX_ACTOR_TRAIL_POINTS) {
    actorTrail.splice(0, actorTrail.length - MAX_ACTOR_TRAIL_POINTS);
  }
};

const clearSceneTrails = (scene) => {
  if (scene) {
    scene.actorTrails = {};
  }
};

const makeDefaultScene = () => ({
  id: nanoid(),
  background: '#ffffff',
  actors: [makeDefaultstem()],
  obstacles: [],
  coloredAreas: [],
  heading: {
    text: '',
    color: '#222',
    size: 38,
  },
  actorTrails: {},
  undoStack: [],
  redoStack: [],
});

// Hard limit to keep performance stable when many scenes are added.
export const MAX_SCENES = 10;

const initialState = {
  scenes: [makeDefaultScene()],
  currentSceneIndex: 0,
  stageGridWidth: GRID_WIDTH,
  stageGridHeight: GRID_HEIGHT,
  simGridWidth: SIM_DEFAULT_GRID_WIDTH,
  simGridHeight: SIM_DEFAULT_GRID_HEIGHT,
  drawLineSettings: loadStoredDrawLineSettings(),
  simulatorRobots: [], // simulator robots (independent scripts)
  currentSimulatorRobotIndex: 0,
  simulatorPath: null, // sampled path grid for line following

  // scene-level undo/redo
  sceneUndoStack: [],
  sceneRedoStack: [],

  selectedBlockCategory: 'motion',
  categoryPanelOpen: false,
  sounds: { pop: './assets/sounds/pop.mp3' },
  backgroundGallery: [
    './assets/backgrounds/bg1.png',
    './assets/backgrounds/bg2.png',
    './assets/backgrounds/bg3.png',
    '#ffffff', '#87CEEB', '#98FB98', '#FFB6C1', '#F0E68C',
  ],
  customSounds: [],
  showHumanDetection: false,
  globalCameraState: 'off',
  videoOpacity: 100,
  cardModeEnabled: false,
  qrScanEnabled: false,
  numberDetectEnabled: false,

  // selection
  selectedSimRobotId: null,
  selectedDevice: null,
  simulatorOpen: false,
  aiChatOpen: false,
  robotModeOpen: false,  // 🤖 ADDED: Robot mode state
  // 🤖 ADD THIS AFTER robotModeOpen:
  robotState: {
    distance: 50,
    temperature: 50,
    humidity: 50,
    potentiometer: 512,
    lineSensorLeft: 500,
    lineSensorRight: 500,
    ledColor: '#FF0000',
    matrixPattern: Array(64).fill(false),
    faceExpression: 0,
    volume: 50,
    serialOutput: []
  },

  // ?? Script-area history (per target: "actor:" or "simRobot:")
  history: {
    byTarget: {
      // "actor:abc": { past: [], present: [], future: [] }
    }
  },

};

const getStageGridWidth = (state) => Math.max(1, state.stageGridWidth || GRID_WIDTH);
const getStageGridHeight = (state) => Math.max(1, state.stageGridHeight || GRID_HEIGHT);
const getStageGridDimensions = (state) => ({
  width: getStageGridWidth(state),
  height: getStageGridHeight(state),
});
const getSimGridWidth = (state) => Math.max(1, state.simGridWidth || SIM_DEFAULT_GRID_WIDTH);
const getSimGridHeight = (state) => Math.max(1, state.simGridHeight || SIM_DEFAULT_GRID_HEIGHT);

// Safe deep clone
const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

// ===== Helpers for Script-area history =====
const cloneScripts = (arr) => JSON.parse(JSON.stringify(arr || []));

const cloneActorsForUndo = (actors = []) =>
  actors.map(actor => ({
    ...actor,
    scripts: cloneScripts(actor.scripts || []),
  }));

const cloneSceneForUndo = (scene) => {
  if (!scene) {
    return {
      actors: [],
      background: null,
      obstacles: [],
      coloredAreas: []
    };
  }
  return {
    actors: cloneActorsForUndo(scene.actors || []),
    background: scene.background,
    obstacles: deepClone(scene.obstacles || []),
    coloredAreas: deepClone(scene.coloredAreas || []),
  };
};
const targetKey = (t) => (t ? `${t.type}:${t.id}` : null);

const resolveTarget = (state, payload = {}) => {
  const scene = state.scenes[state.currentSceneIndex];

  if (payload?.robotId) {
    const r = state.simulatorRobots.find(x => x.id === payload.robotId);
    return r ? { type: 'simRobot', id: r.id, scripts: r.scripts || [] } : null;
  }
  if (payload?.actorId) {
    const a = scene?.actors?.find?.(x => x.id === payload.actorId);
    return a ? { type: 'actor', id: a.id, scripts: a.scripts || [] } : null;
  }
  return null; // when called without payload ? we?ll use scene-level behavior
};

const ensureBucket = (state, key) => {
  if (!state.history.byTarget[key]) {
    state.history.byTarget[key] = { past: [], present: [], future: [] };
  }
  return state.history.byTarget[key];
};

const setTargetScripts = (state, t, scripts) => {
  if (!t) return;
  if (t.type === 'simRobot') {
    const idx = state.simulatorRobots.findIndex(r => r.id === t.id);
    if (idx !== -1) state.simulatorRobots[idx].scripts = scripts;
  } else {
    const scene = state.scenes[state.currentSceneIndex];
    const aidx = scene.actors.findIndex(a => a.id === t.id);
    if (aidx !== -1) scene.actors[aidx].scripts = scripts;
  }
};

const syncHistoryPresentFor = (state, t) => {
  if (!t) return;
  const key = targetKey(t);
  const bucket = ensureBucket(state, key);
  bucket.present = cloneScripts(t.scripts);
};

const sceneSlice = createSlice({
  name: 'scene',
  initialState,
  reducers: {
    // ===== Global scene (array) undo/redo =====
    pushSceneUndoState(state) {
      state.sceneUndoStack.push({
        scenes: deepClone(state.scenes),
        currentSceneIndex: state.currentSceneIndex,
      });
      if (state.sceneUndoStack.length > 20) state.sceneUndoStack.shift();
      state.sceneRedoStack = [];
    },

    undoSceneAction(state) {
      if (!state.sceneUndoStack.length) return;
      const prev = state.sceneUndoStack.pop();
      state.sceneRedoStack.push({
        scenes: deepClone(state.scenes),
        currentSceneIndex: state.currentSceneIndex,
      });
      state.scenes = prev.scenes;
      state.currentSceneIndex = prev.currentSceneIndex;
    },

    redoSceneAction(state) {
      if (!state.sceneRedoStack.length) return;
      const next = state.sceneRedoStack.pop();
      state.sceneUndoStack.push({
        scenes: deepClone(state.scenes),
        currentSceneIndex: state.currentSceneIndex,
      });
      state.scenes = next.scenes;
      state.currentSceneIndex = next.currentSceneIndex;
    },

    // ===== Sprite speech messages =====

    showSpriteMessage: (state, action) => {
      const { actorId, message } = action.payload;
      const scene = state.scenes[state.currentSceneIndex];
      const actor = scene.actors.find(a => a.id === actorId);
      if (actor) {
        console.log("Showing message:", message, "for actor:", actorId);
        actor.speechMessage = message;
      }
    },
    hideSpriteMessage: (state, action) => {
      const { actorId } = action.payload;
      const scene = state.scenes[state.currentSceneIndex];
      const actor = scene.actors.find(a => a.id === actorId);
      if (actor) {
        console.log("Hiding message for actor:", actorId);
        actor.speechMessage = null;
      }
    },

    // ===== UI state =====
    setSelectedBlockCategory(state, action) {
      const validCategories = [
        'start', 'motion', 'looks', 'sound', 'control',
        'sensors', 'device', 'humandetection', 'facesensing', 'end',
        'otto', 'esp32', 'robot', 'stembot'  // 🤖 ADDED
      ];

      // Prevent sensors category if simulator not open
      if (action.payload === 'sensors' && !state.simulatorOpen) {
        return;
      }

      // 🤖 ADDED: Prevent robot category if robot mode not open
      if (action.payload === 'robot' && !state.robotModeOpen) {
        return;
      }

      // AI chat category gated by AI toggle
      if (action.payload === 'stembot' && !state.aiChatOpen) {
        return;
      }

      if (validCategories.includes(action.payload)) {
        state.selectedBlockCategory = action.payload;
      }
    },


    toggleCategoryPanel(state) {
      state.categoryPanelOpen = !state.categoryPanelOpen;
    },

    setSimulatorOpen(state, action) {
      const nextOpen = !!action.payload;
      state.simulatorOpen = nextOpen;
      // Prevent simulator + robot mode overlap.
      if (nextOpen) {
        state.robotModeOpen = false;
      }
    },

    setRobotModeOpen(state, action) {
      const nextOpen = !!action.payload;
      state.robotModeOpen = nextOpen;
      // Prevent simulator + robot mode overlap.
      if (nextOpen) {
        state.simulatorOpen = false;
      }
    },

    setAiChatOpen(state, action) {
      const nextOpen = !!action.payload;
      state.aiChatOpen = nextOpen;
      if (!nextOpen && state.selectedBlockCategory === 'stembot') {
        state.selectedBlockCategory = 'motion';
      }
    },

    setRobotState(state, action) {
      state.robotState = { ...state.robotState, ...action.payload };
    },

    updateActorCustomData(state, action) {
      const { actorId, robotId, data } = action.payload || {};
      if (!data) return;
      if (robotId) {
        const robot = state.simulatorRobots.find(r => r.id === robotId);
        if (robot) {
          Object.assign(robot, data);
        }
        return;
      }
      if (actorId) {
        const scene = state.scenes[state.currentSceneIndex];
        const actor = scene?.actors?.find(a => a.id === actorId);
        if (actor) {
          Object.assign(actor, data);
        }
      }
    },

    setStageGridSize(state, action) {
      const nextWidth = Math.max(1, Math.floor(action.payload?.gridWidth ?? 0));
      const nextHeight = Math.max(1, Math.floor(action.payload?.gridHeight ?? 0));
      if (!Number.isFinite(nextWidth) || !Number.isFinite(nextHeight)) {
        return;
      }
      const prevWidth = Math.max(1, state.stageGridWidth || nextWidth);
      const prevHeight = Math.max(1, state.stageGridHeight || nextHeight);
      if (
        prevWidth === nextWidth &&
        prevHeight === nextHeight
      ) {
        return;
      }

      const scaleX = prevWidth > 0 ? nextWidth / prevWidth : 1;
      const scaleY = prevHeight > 0 ? nextHeight / prevHeight : 1;
      const clampCoord = (value, max) => {
        if (!Number.isFinite(value)) return 0;
        const upper = Math.max(0, max - 1);
        return Math.max(0, Math.min(Math.round(value), upper));
      };
      const scalePoint = (point) => {
        if (!point) return point;
        return {
          ...point,
          x: clampCoord((point.x ?? 0) * scaleX, nextWidth),
          y: clampCoord((point.y ?? 0) * scaleY, nextHeight),
        };
      };

      state.scenes = state.scenes.map(scene => {
        if (!scene) return scene;

        let actorsChanged = false;
        const updatedActors = Array.isArray(scene.actors)
          ? scene.actors.map(actor => {
            if (!actor) return actor;
            const nextActor = { ...actor };
            const nextX = clampCoord((actor.x ?? 0) * scaleX, nextWidth);
            const nextY = clampCoord((actor.y ?? 0) * scaleY, nextHeight);
            if (nextX !== actor.x || nextY !== actor.y) {
              nextActor.x = nextX;
              nextActor.y = nextY;
              actorsChanged = true;
            }
            if (actor.homePosition) {
              const scaled = scalePoint(actor.homePosition);
              if (scaled.x !== actor.homePosition.x || scaled.y !== actor.homePosition.y) {
                nextActor.homePosition = scaled;
                actorsChanged = true;
              }
            }
            if (actor.originalPosition) {
              const scaled = scalePoint(actor.originalPosition);
              if (scaled.x !== actor.originalPosition.x || scaled.y !== actor.originalPosition.y) {
                nextActor.originalPosition = scaled;
                actorsChanged = true;
              }
            }
            if (actor.wrapAnchors) {
              const nextAnchors = {
                left: scalePoint(actor.wrapAnchors.left),
                right: scalePoint(actor.wrapAnchors.right),
                top: scalePoint(actor.wrapAnchors.top),
                bottom: scalePoint(actor.wrapAnchors.bottom),
              };
              const anchorsChangedFlag = ['left', 'right', 'top', 'bottom'].some(key => {
                const current = actor.wrapAnchors?.[key];
                const next = nextAnchors[key];
                return (current?.x !== next?.x) || (current?.y !== next?.y);
              });
              if (anchorsChangedFlag) {
                nextActor.wrapAnchors = nextAnchors;
                actorsChanged = true;
              }
            }
            return nextActor;
          })
          : scene.actors;

        let obstaclesChanged = false;
        const updatedObstacles = Array.isArray(scene.obstacles)
          ? scene.obstacles.map(obstacle => {
            if (!obstacle) return obstacle;
            const nextObstacle = { ...obstacle };
            const nextX = clampCoord((obstacle.x ?? 0) * scaleX, nextWidth);
            const nextY = clampCoord((obstacle.y ?? 0) * scaleY, nextHeight);
            if (nextX !== obstacle.x || nextY !== obstacle.y) {
              nextObstacle.x = nextX;
              nextObstacle.y = nextY;
              obstaclesChanged = true;
            }
            return nextObstacle;
          })
          : scene.obstacles;

        let trailsChanged = false;
        let updatedTrails = scene.actorTrails;
        if (scene.actorTrails && typeof scene.actorTrails === 'object') {
          const nextTrails = {};
          Object.entries(scene.actorTrails).forEach(([actorId, points]) => {
            if (!Array.isArray(points)) {
              nextTrails[actorId] = points;
              return;
            }
            let pointsChanged = false;
            const nextPoints = points.map(point => {
              const scaled = scalePoint(point);
              if (scaled.x !== point.x || scaled.y !== point.y) {
                pointsChanged = true;
              }
              return scaled;
            });
            nextTrails[actorId] = pointsChanged ? nextPoints : points;
            if (pointsChanged) {
              trailsChanged = true;
            }
          });
          updatedTrails = trailsChanged ? nextTrails : scene.actorTrails;
        }

        if (!actorsChanged && !obstaclesChanged && !trailsChanged) {
          return scene;
        }

        return {
          ...scene,
          actors: actorsChanged ? updatedActors : scene.actors,
          obstacles: obstaclesChanged ? updatedObstacles : scene.obstacles,
          actorTrails: trailsChanged ? updatedTrails : scene.actorTrails,
        };
      });

      state.stageGridWidth = nextWidth;
      state.stageGridHeight = nextHeight;
    },

    setSimulatorGridSize(state, action) {
      const nextWidth = Math.max(1, Math.floor(action.payload?.gridWidth ?? 0));
      const nextHeight = Math.max(1, Math.floor(action.payload?.gridHeight ?? 0));
      if (!Number.isFinite(nextWidth) || !Number.isFinite(nextHeight)) {
        return;
      }
      const prevWidth = getSimGridWidth(state);
      const prevHeight = getSimGridHeight(state);
      if (prevWidth === nextWidth && prevHeight === nextHeight) {
        return;
      }

      const scaleX = prevWidth > 0 ? nextWidth / prevWidth : 1;
      const scaleY = prevHeight > 0 ? nextHeight / prevHeight : 1;
      const clampCoord = (value, max) => {
        if (!Number.isFinite(value)) return 0;
        const upper = Math.max(0, max - 1);
        return Math.max(0, Math.min(Math.round(value), upper));
      };

      if (Array.isArray(state.simulatorRobots)) {
        state.simulatorRobots = state.simulatorRobots.map(robot => {
          if (!robot) return robot;
          const nextX = clampCoord((robot.x ?? 0) * scaleX, nextWidth);
          const nextY = clampCoord((robot.y ?? 0) * scaleY, nextHeight);
          if (nextX === robot.x && nextY === robot.y) {
            return robot;
          }
          return {
            ...robot,
            x: nextX,
            y: nextY,
          };
        });
      }

      state.simulatorPath = null;
      state.simGridWidth = nextWidth;
      state.simGridHeight = nextHeight;
    },

    setSceneHeading(state, action) {
      const { sceneIndex, heading } = action.payload || {};
      if (!heading || typeof heading !== 'object') return;
      const index = Number.isInteger(sceneIndex) ? sceneIndex : state.currentSceneIndex;
      if (index < 0 || index >= state.scenes.length) return;
      const scene = state.scenes[index];
      if (!scene) return;
      const prevHeading = scene.heading || { text: '', color: '#222', size: 38 };
      scene.heading = {
        ...prevHeading,
        ...heading,
      };
    },

    // ===== UNDO/REDO ENTRYPOINT (script-area or scene-level) =====
    // - If payload has {actorId} or {robotId}: snapshot scripts for that target
    // - Else: snapshot whole scene (original behavior)
    pushUndoState(state, { payload }) {
      const t = resolveTarget(state, payload);
      if (t) {
        const key = targetKey(t);
        const bucket = ensureBucket(state, key);
        bucket.past.push(cloneScripts(t.scripts));
        bucket.future = [];
        bucket.present = cloneScripts(t.scripts);
        return;
      }
      const scene = state.scenes[state.currentSceneIndex];
      if (!scene) return;
      scene.undoStack.push(cloneSceneForUndo(scene));
      if (scene.undoStack.length > 20) scene.undoStack.shift();
      scene.redoStack = [];
    },

    // Script-only UNDO / REDO
    undo(state, { payload }) {
      const t = resolveTarget(state, payload);
      if (!t) return;
      const key = targetKey(t);
      const bucket = ensureBucket(state, key);
      if (!bucket.past.length) return;

      const current = cloneScripts(t.scripts);
      const previous = bucket.past.pop();

      bucket.future.push(current);
      bucket.present = cloneScripts(previous);

      setTargetScripts(state, t, cloneScripts(previous));
    },

    redo(state, { payload }) {
      const t = resolveTarget(state, payload);
      if (!t) return;
      const key = targetKey(t);
      const bucket = ensureBucket(state, key);
      if (!bucket.future.length) return;

      const current = cloneScripts(t.scripts);
      const next = bucket.future.pop();

      bucket.past.push(current);
      bucket.present = cloneScripts(next);

      setTargetScripts(state, t, cloneScripts(next));
    },

    // ===== Per-scene undo/redo (actors/background etc.) =====
    undoLastAction(state) {
      const scene = state.scenes[state.currentSceneIndex];
      if (!scene?.undoStack?.length) return;
      const previous = scene.undoStack.pop();
      scene.redoStack.push(cloneSceneForUndo(scene));
      scene.actors = previous.actors;
      scene.background = previous.background;
      scene.obstacles = previous.obstacles || [];
      scene.coloredAreas = previous.coloredAreas || [];
    },

    redoLastAction(state) {
      const scene = state.scenes[state.currentSceneIndex];
      if (!scene?.redoStack?.length) return;
      const next = scene.redoStack.pop();
      scene.undoStack.push(cloneSceneForUndo(scene));
      scene.actors = next.actors;
      scene.background = next.background;
      scene.obstacles = next.obstacles || [];
      scene.coloredAreas = next.coloredAreas || [];
    },

    // ===== Scene CRUD =====
    addScene(state) {
      if (state.scenes.length >= MAX_SCENES) {
        // Limit reached; no-op to prevent memory/CPU blow-up.
        return;
      }
      state.scenes.push(makeDefaultScene());
      state.currentSceneIndex = state.scenes.length - 1;
    },

    removeScene(state, action) {
      const idx = action.payload;
      if (state.scenes.length <= 1) return;
      if (idx < 0 || idx >= state.scenes.length) return;

      state.sceneUndoStack.push({
        scenes: deepClone(state.scenes),
        currentSceneIndex: state.currentSceneIndex,
      });
      state.sceneRedoStack = [];

      state.scenes.splice(idx, 1);
      if (state.currentSceneIndex >= state.scenes.length) {
        state.currentSceneIndex = state.scenes.length - 1;
      } else if (state.currentSceneIndex > idx) {
        state.currentSceneIndex--;
      }
    },

    switchScene(state, action) {
      const idx = action.payload;
      if (idx >= 0 && idx < state.scenes.length) {
        state.currentSceneIndex = idx;
      }
    },

    // ===== Obstacles =====
    addObstacle(state, action) {
      const obstacle = action.payload;
      const currentScene = state.scenes[state.currentSceneIndex];
      if (currentScene) {
        if (!currentScene.obstacles) currentScene.obstacles = [];
        currentScene.obstacles.push({
          id: obstacle.id || `obstacle-${Date.now()}-${Math.random()}`,
          shape: obstacle.shape,
          x: obstacle.x || Math.floor(Math.random() * 18) + 1,
          y: obstacle.y || Math.floor(Math.random() * 15) + 1,
          ...obstacle
        });
      }
    },

    removeObstacle(state, action) {
      const obstacleId = action.payload;
      const currentScene = state.scenes[state.currentSceneIndex];
      if (currentScene && currentScene.obstacles) {
        currentScene.obstacles = currentScene.obstacles.filter(o => o.id !== obstacleId);
      }
    },

    moveObstacle(state, action) {
      const { id, x, y } = action.payload;
      const currentScene = state.scenes[state.currentSceneIndex];
      if (currentScene && currentScene.obstacles) {
        const obstacle = currentScene.obstacles.find(o => o.id === id);
        if (obstacle) {
          obstacle.x = x;
          obstacle.y = y;
        }
      }
    },

    // ===== Colored areas (non-blocking) =====
    addColoredArea(state, action) {
      const coloredArea = action.payload;
      const currentScene = state.scenes[state.currentSceneIndex];
      if (currentScene) {
        if (!currentScene.coloredAreas) currentScene.coloredAreas = [];
        currentScene.coloredAreas.push({
          id: coloredArea.id || `coloredArea-${Date.now()}-${Math.random()}`,
          color: coloredArea.color || '#ffeb3b',
          x: coloredArea.x || Math.floor(Math.random() * 18) + 1,
          y: coloredArea.y || Math.floor(Math.random() * 15) + 1,
          width: coloredArea.width || 60,
          height: coloredArea.height || 60,
          type: 'coloredArea',
          blocking: false
        });
      }
    },

    removeColoredArea(state, action) {
      const coloredAreaId = action.payload;
      const currentScene = state.scenes[state.currentSceneIndex];
      if (currentScene && currentScene.coloredAreas) {
        currentScene.coloredAreas = currentScene.coloredAreas.filter(c => c.id !== coloredAreaId);
      }
    },

    moveColoredArea(state, action) {
      const { id, x, y } = action.payload;
      const currentScene = state.scenes[state.currentSceneIndex];
      if (currentScene && currentScene.coloredAreas) {
        const coloredArea = currentScene.coloredAreas.find(c => c.id === id);
        if (coloredArea) {
          coloredArea.x = x;
          coloredArea.y = y;
        }
      }
    },

    // ===== Actors (stage) =====
    addActor(state, action) {
      const scene = state.scenes[state.currentSceneIndex];
      if (!scene) return;

      scene.undoStack.push(cloneSceneForUndo(scene));
      scene.redoStack = [];

      const {
        id, name, image, x, y, direction = 0, width, height, scripts,
      } = action.payload || {};

      const actor = {
        id: id ?? nanoid(),
        name: name ?? 'Actor',
        image: image ?? '',
        x: Number.isFinite(x) ? x : CENTER_X,
        y: Number.isFinite(y) ? y : CENTER_Y,
        direction,
        width,
        height,
        scripts: scripts ?? [],
        visible: true,
        speechSettings: action.payload?.speechSettings || makeDefaultSpeechSettings(),
      };

      scene.actors.push(actor);
      ensureActorTrails(scene);
      scene.actorTrails[actor.id] = [];
    },

    removeActor(state, action) {
      const scene = state.scenes[state.currentSceneIndex];
      if (!scene) return;
      if (scene.actors.length <= 1) return;

      scene.undoStack.push(cloneSceneForUndo(scene));
      scene.redoStack = [];

      const targetId = action.payload.actorId;
      scene.actors = scene.actors.filter(a => a.id !== targetId);
      if (scene.actorTrails) {
        delete scene.actorTrails[targetId];
      }
    },

    moveActor(state, action) {
      const { actorId, dx, dy, fromScript, resetPosition } = action.payload;
      const scene = state.scenes[state.currentSceneIndex];
      if (!scene) return;

      const actor = scene.actors.find(a => a.id === actorId);
      if (!actor) return;

      const gridWidth = getStageGridWidth(state);
      const gridHeight = getStageGridHeight(state);
      const clampX = (x) => Math.min(Math.max(0, x), gridWidth - 1);
      const clampY = (y) => Math.min(Math.max(0, y), gridHeight - 1);
      const wrapX = (x) => ((x % gridWidth) + gridWidth) % gridWidth;
      const wrapY = (y) => ((y % gridHeight) + gridHeight) % gridHeight;

      actor.originalPosition = actor.originalPosition || { x: clampX(actor.x), y: clampY(actor.y) };
      actor.homePosition = actor.homePosition || { x: actor.originalPosition.x, y: actor.originalPosition.y };

      // Ensure directional wrap anchors exist (left/right/top/bottom)
      const midX = Math.floor(gridWidth / 2);
      const midY = Math.floor(gridHeight / 2);
      actor.wrapAnchors = actor.wrapAnchors || {
        left: { x: 0, y: clampY(midY) },
        right: { x: clampX(gridWidth - 1), y: clampY(midY) },
        top: { x: clampX(midX), y: 0 },
        bottom: { x: clampX(midX), y: clampY(gridHeight - 1) },
      };

      if (!actor.homePosition) {
        actor.homePosition = { x: clampX(actor.x), y: clampY(actor.y) };
      }

      if (!fromScript) {
        scene.undoStack.push(cloneSceneForUndo(scene));
        scene.redoStack = [];
      }

      if (resetPosition) {
        const home = actor.originalPosition || actor.homePosition || { x: midX, y: midY };
        actor.x = clampX(home.x);
        actor.y = clampY(home.y);
        return;
      }

      const nextX = actor.x + dx;
      const nextY = actor.y + dy;

      let newX = nextX;
      let newY = nextY;
      let didWrap = false;

      if (fromScript) {
        // ScratchJr-style wraparound: exit one edge, enter on the opposite edge.
        const wrappedX = wrapX(nextX);
        const wrappedY = wrapY(nextY);
        didWrap = wrappedX !== nextX || wrappedY !== nextY;
        newX = wrappedX;
        newY = wrappedY;
      } else {
        newX = clampX(newX);
        newY = clampY(newY);
        actor.homePosition = { x: newX, y: newY };
      }

      actor.x = clampX(newX);
      actor.y = clampY(newY);
      actor.lastMoveWrapped = didWrap;
      actor.disableTransition = !!fromScript;
      if (state.drawLineSettings?.enabled) {
        appendActorTrailPoint(scene, actor.id, actor.x, actor.y);
      }
    },

    setActorLanguage(state, action) {
      const { actorId, robotId, lang } = action.payload || {};
      if (!lang) return;
      if (actorId) {
        const scene = state.scenes[state.currentSceneIndex];
        const actor = scene?.actors?.find(a => a.id === actorId);
        if (!actor) return;
        ensureActorSpeechSettings(actor);
        actor.speechSettings.lang = lang;
        return;
      }
      if (robotId) {
        const robot = state.simulatorRobots.find(r => r.id === robotId);
        if (!robot) return;
        ensureActorSpeechSettings(robot);
        robot.speechSettings.lang = lang;
      }
    },

    setActorPosition(state, action) {
      const { actorId, x, y, fromScript } = action.payload;
      const scene = state.scenes[state.currentSceneIndex];
      if (!scene) return;

      const actor = scene.actors.find(a => a.id === actorId);
      if (!actor) return;

      const gridWidth = getStageGridWidth(state);
      const gridHeight = getStageGridHeight(state);
      const clampX = (vx) => Math.min(Math.max(0, vx), gridWidth - 1);
      const clampY = (vy) => Math.min(Math.max(0, vy), gridHeight - 1);

      if (!fromScript) {
        scene.undoStack.push(cloneSceneForUndo(scene));
        scene.redoStack = [];
      }

      const newX = clampX(x);
      const newY = clampY(y);

      actor.x = newX;
      actor.y = newY;
      actor.disableTransition = !!fromScript;

      if (!fromScript) {
        actor.homePosition = { x: newX, y: newY };
      }
      if (state.drawLineSettings?.enabled) {
        appendActorTrailPoint(scene, actor.id, actor.x, actor.y);
      }
    },

    rotateActor(state, action) {
      const { actorId, degrees, fromScript } = action.payload;
      const scene = state.scenes[state.currentSceneIndex];
      if (!scene) return;

      const actor = scene.actors.find(a => a.id === actorId);
      if (!actor) return;

      if (!fromScript) {
        scene.undoStack.push(cloneSceneForUndo(scene));
        scene.redoStack = [];
      }

      let newDir = (actor.direction + degrees) % 360;
      if (newDir < 0) newDir += 360;
      actor.direction = newDir;
    },

    scaleActor(state, action) {
      const { actorId, scale, fromScript, reset } = action.payload;
      const scene = state.scenes[state.currentSceneIndex];
      if (!scene) return;

      const actor = scene.actors.find(a => a.id === actorId);
      if (!actor) return;

      if (!fromScript) {
        scene.undoStack.push(cloneSceneForUndo(scene));
        scene.redoStack = [];
      }

      const minSize = 0.5;
      const maxSize = 4.0;

      if (reset) {
        actor.size = 1;
      } else {
        const newSize = (actor.size || 1) * scale;
        actor.size = Math.min(Math.max(newSize, minSize), maxSize);
      }
    },

    resetActorSize(state, action) {
      const { actorId, fromScript } = action.payload;
      const scene = state.scenes[state.currentSceneIndex];
      if (!scene) return;

      const actor = scene.actors.find(a => a.id === actorId);
      if (!actor) return;

      if (!fromScript) {
        scene.undoStack.push(cloneSceneForUndo(scene));
        scene.redoStack = [];
      }

      actor.size = 1;
    },

    disappearActor(state, action) {
      const scene = state.scenes[state.currentSceneIndex];
      if (!scene) return;

      const actor = scene.actors.find((a) => a.id === action.payload.actorId);
      if (actor) {
        actor.visible = false;
      }
    },

    reappearActor(state, action) {
      const scene = state.scenes[state.currentSceneIndex];
      if (!scene) return;

      const actor = scene.actors.find((a) => a.id === action.payload.actorId);
      if (actor) {
        actor.visible = true;
      }
    },

    // ===== Background =====
    setBackground(state, action) {
      const scene = state.scenes[state.currentSceneIndex];
      if (scene) {
        scene.background = action.payload;
      }
    },

    addBackgroundToGallery(state, action) {
      const url = action.payload;
      if (!state.backgroundGallery.includes(url)) {
        state.backgroundGallery.push(url);
      }
    },

    removeBackgroundFromGallery(state, action) {
      state.backgroundGallery = state.backgroundGallery.filter(bg => bg !== action.payload);
    },

    cycleNextBackground(state) {
      const scene = state.scenes[state.currentSceneIndex];
      if (!scene) return;

      scene.undoStack.push(cloneSceneForUndo(scene));
      if (scene.undoStack.length > 20) scene.undoStack.shift();
      scene.redoStack = [];

      const gallery = state.backgroundGallery;
      const currentBg = scene.background;

      let currentIndex = gallery.findIndex(bg => bg === currentBg);
      if (currentIndex === -1) currentIndex = -1;

      const nextIndex = (currentIndex + 1) % gallery.length;
      scene.background = gallery[nextIndex];
    },

    // ===== Scripts (stage) =====
    addBlockToScript(state, action) {
      const { actorId, block, index } = action.payload;
      const scene = state.scenes[state.currentSceneIndex];
      const actor = scene?.actors.find(a => a.id === actorId);
      if (!actor) return;

      const referenceWidth = Math.max(1, Number.isFinite(block.layoutRefWidth) ? block.layoutRefWidth : DEFAULT_SCRIPTAREA_WIDTH);
      const referenceHeight = Math.max(1, Number.isFinite(block.layoutRefHeight) ? block.layoutRefHeight : DEFAULT_SCRIPTAREA_HEIGHT);
      const safeX = Math.max(0, Math.min(Number.isFinite(block.x) ? block.x : 50, referenceWidth));
      const safeY = Math.max(0, Math.min(Number.isFinite(block.y) ? block.y : 50, referenceHeight));
      const xRatio = Number.isFinite(block.xRatio) ? block.xRatio : safeX / referenceWidth;
      const yRatio = Number.isFinite(block.yRatio) ? block.yRatio : safeY / referenceHeight;

      const newBlock = {
        id: nanoid(),
        type: block.name,
        ...block,
        x: safeX,
        y: safeY,
        layoutRefWidth: referenceWidth,
        layoutRefHeight: referenceHeight,
        xRatio,
        yRatio,
        nextBlockId: null,  // ID of block connected to the right
        // Existing fields
        steps: block.category === 'move' ? block.steps ?? 10 : undefined,
        angle: block.category === 'turn' ? block.angle ?? 90 : undefined,
        duration: block.category === 'wait' ? block.duration ?? 1000 : undefined,
        soundData: block.soundData || null,
        audioURL: block.soundData?.audioURL || null,
        lowFrequency: block.type === 'obstaclesound' ? block.lowFrequency ?? 1 : undefined,
        highFrequency: block.type === 'obstaclesound' ? block.highFrequency ?? 99 : undefined,
        pathColor: block.pathColor || (block.type === 'setPathColor' ? '#000000' : undefined),
        maxSteps: block.maxSteps || (block.type === 'followPath' ? 50 : undefined),
      };


      const desired = Number.isInteger(index) ? index : actor.scripts.length;
      const clampedIndex = Math.max(0, Math.min(desired, actor.scripts.length));
      actor.scripts.splice(clampedIndex, 0, newBlock);

      const t = resolveTarget(state, { actorId });
      if (t) syncHistoryPresentFor(state, t);
    },

    addBlockToContainer(state, action) {
      const { actorId, robotId, containerId, block, index } = action.payload;

      const scene = state.scenes[state.currentSceneIndex];

      // Find the target (actor or robot)
      let targetScripts;
      if (robotId) {
        const robot = state.simulatorRobots.find(r => r.id === robotId);
        if (!robot) return;
        targetScripts = robot.scripts;
      } else if (actorId) {
        const actor = scene?.actors?.find(a => a.id === actorId);
        if (!actor) return;
        targetScripts = actor.scripts;
      } else {
        return;
      }

      // Recursively find the container block
      function findContainer(blocks, id) {
        for (const b of blocks) {
          if (b.id === id) return b;
          if (b.children && b.children.length > 0) {
            const found = findContainer(b.children, id);
            if (found) return found;
          }
        }
        return null;
      }

      const container = findContainer(targetScripts, containerId);
      if (!container || !container.children) return;

      const newBlock = {
        id: nanoid(),
        type: block.name,
        ...block,
        steps: block.category === "move" ? block.steps ?? 10 : undefined,
        children: block.isContainer ? [] : undefined,
      };

      const insertIndex = typeof index === "number" ? index : container.children.length;
      container.children.splice(insertIndex, 0, newBlock);

      const t = resolveTarget(state, { actorId, robotId });
      if (t) syncHistoryPresentFor(state, t);
    },

    // Remove block from container
    removeBlockFromContainer(state, action) {
      const { actorId, robotId, containerId, blockId } = action.payload;

      const scene = state.scenes[state.currentSceneIndex];

      let targetScripts;
      if (robotId) {
        const robot = state.simulatorRobots.find(r => r.id === robotId);
        if (!robot) return;
        targetScripts = robot.scripts;
      } else if (actorId) {
        const actor = scene?.actors?.find(a => a.id === actorId);
        if (!actor) return;
        targetScripts = actor.scripts;
      } else {
        return;
      }

      function findAndRemove(blocks, containerId, blockId) {
        for (const b of blocks) {
          if (b.id === containerId && b.children) {
            const index = b.children.findIndex(c => c.id === blockId);
            if (index !== -1) {
              b.children.splice(index, 1);
              return true;
            }
          }
          if (b.children && b.children.length > 0) {
            if (findAndRemove(b.children, containerId, blockId)) return true;
          }
        }
        return false;
      }

      findAndRemove(targetScripts, containerId, blockId);

      const t = resolveTarget(state, { actorId, robotId });
      if (t) syncHistoryPresentFor(state, t);
    },

    // Move block between containers
    moveBlockBetweenContainers(state, action) {
      const { actorId, robotId, fromContainerId, toContainerId, blockId, toIndex } = action.payload;

      const scene = state.scenes[state.currentSceneIndex];

      let targetScripts;
      if (robotId) {
        const robot = state.simulatorRobots.find(r => r.id === robotId);
        if (!robot) return;
        targetScripts = robot.scripts;
      } else if (actorId) {
        const actor = scene?.actors?.find(a => a.id === actorId);
        if (!actor) return;
        targetScripts = actor.scripts;
      } else {
        return;
      }

      // Find and remove the block
      let movedBlock = null;

      function findAndExtract(blocks, containerId, blockId) {
        if (containerId === "main") {
          const index = blocks.findIndex(b => b.id === blockId);
          if (index !== -1) {
            movedBlock = blocks.splice(index, 1)[0];
            return true;
          }
        } else {
          for (const b of blocks) {
            if (b.id === containerId && b.children) {
              const index = b.children.findIndex(c => c.id === blockId);
              if (index !== -1) {
                movedBlock = b.children.splice(index, 1)[0];
                return true;
              }
            }
            if (b.children && b.children.length > 0) {
              if (findAndExtract(b.children, containerId, blockId)) return true;
            }
          }
        }
        return false;
      }

      findAndExtract(targetScripts, fromContainerId, blockId);

      if (!movedBlock) return;

      // Insert into new location
      function findAndInsert(blocks, containerId, block, index) {
        if (containerId === "main") {
          blocks.splice(index, 0, block);
          return true;
        } else {
          for (const b of blocks) {
            if (b.id === containerId && b.children) {
              b.children.splice(index, 0, block);
              return true;
            }
            if (b.children && b.children.length > 0) {
              if (findAndInsert(b.children, containerId, block, index)) return true;
            }
          }
        }
        return false;
      }

      findAndInsert(targetScripts, toContainerId, movedBlock, toIndex);

      const t = resolveTarget(state, { actorId, robotId });
      if (t) syncHistoryPresentFor(state, t);
    },

    clearScript(state, action) {
      const scene = state.scenes[state.currentSceneIndex];
      const actor = scene?.actors.find(a => a.id === action.payload.actorId);
      if (!actor) return;

      // script-history snapshot (not scene snapshot)
      const t = { type: 'actor', id: actor.id, scripts: actor.scripts || [] };
      const key = `actor:${actor.id}`;
      const bucket = ensureBucket(state, key);

      bucket.past.push(cloneScripts(actor.scripts));
      bucket.future = [];
      actor.scripts = [];
      bucket.present = [];
    },

    updateBlockMessage: (state, action) => {
      const { actorId, blockId, message } = action.payload;
      const scene = state.scenes[state.currentSceneIndex];
      const actor = scene.actors.find(a => a.id === actorId);
      if (actor) {
        const block = actor.scripts.find(b => b.id === blockId);
        if (block) {
          block.message = message;
        }
      }
    },

    updateBlockCustomData(state, action) {
      const { actorId, robotId, blockId, data } = action.payload || {};
      if (!blockId || !data) return;
      const target = resolveTarget(state, { actorId, robotId });
      if (!target || !Array.isArray(target.scripts)) return;
      const block = target.scripts.find(b => b.id === blockId);
      if (block) {
        Object.assign(block, data);
      }
    },

    updateBlockColor(state, action) {
      const { actorId, blockId, color, property = 'pathColor' } = action.payload;
      const scene = state.scenes[state.currentSceneIndex];
      const actor = scene?.actors.find(a => a.id === actorId);
      if (!actor) return;

      const block = actor.scripts.find(b => b.id === blockId);
      if (block) {
        if (property === 'messageColor') {
          const normalized = normalizeMessageColor(color || DEFAULT_MESSAGE_COLOR);
          block.messageColor = normalized;
          block.icon = getMessageIconPath(block.type, normalized) || block.icon;
        } else {
          block[property] = color;
        }
      }

      const t = resolveTarget(state, { actorId });
      if (t) syncHistoryPresentFor(state, t);
    },

    updateBlockCount(state, action) {
      const { actorId, blockId, newCount, property = 'count', value } = action.payload;
      const scene = state.scenes[state.currentSceneIndex];
      const actor = scene?.actors.find(a => a.id === actorId);
      if (!actor) return;

      const block = actor.scripts.find(b => b.id === blockId);
      if (block) {
        const stringProps = new Set(['pinConfig', 'part', 'finger', 'expression']);
        if (stringProps.has(property)) {
          block[property] = value ?? block[property];
        } else if (property === 'opacity') {
          block[property] = Math.max(0, Math.min(100, newCount));
        } else {
          block[property] = Math.max(0, newCount);
        }
      }

      const t = resolveTarget(state, { actorId });
      if (t) syncHistoryPresentFor(state, t);
    },

    updateObstacleFrequency(state, action) {
      const { actorId, blockId, lowFrequency, highFrequency } = action.payload;
      const scene = state.scenes[state.currentSceneIndex];
      const actor = scene?.actors.find(a => a.id === actorId);
      if (!actor) return;

      const block = actor.scripts.find(b => b.id === blockId);
      if (block) {
        if (lowFrequency !== undefined) {
          block.lowFrequency = Math.min(Math.max(1, lowFrequency), 99);
        }
        if (highFrequency !== undefined) {
          block.highFrequency = Math.min(Math.max(1, highFrequency), 99);
        }
      }

      const t = resolveTarget(state, { actorId });
      if (t) syncHistoryPresentFor(state, t);
    },

    updateCameraState(state, action) {
      const { actorId, blockId, cameraState } = action.payload;
      const scene = state.scenes[state.currentSceneIndex];
      const actor = scene.actors.find(a => a.id === actorId);
      if (actor) {
        const block = actor.scripts.find(b => b.id === blockId);
        if (block) {
          block.cameraState = cameraState;
        }
      }

      const t = resolveTarget(state, { actorId });
      if (t) syncHistoryPresentFor(state, t);
    },

    setCameraState(state, action) {
      state.globalCameraState = action.payload;
    },

    setSelectedDevice(state, action) {
      state.selectedDevice = action.payload;
    },

    // ===== Custom sounds =====
    addCustomSound(state, action) {
      const customSound = {
        id: action.payload.id || nanoid(),
        name: action.payload.name,
        audioURL: action.payload.audioURL,
        audioBlob: action.payload.audioBlob,
        type: 'custom',
        createdAt: Date.now(),
      };
      state.customSounds.push(customSound);

      if (state.customSounds.length > 10) {
        const removedSound = state.customSounds.shift();
        if (removedSound.audioURL) {
          URL.revokeObjectURL(removedSound.audioURL);
        }
      }
    },

    removeCustomSound(state, action) {
      const soundId = action.payload;
      const soundIndex = state.customSounds.findIndex(sound => sound.id === soundId);
      if (soundIndex !== -1) {
        const removedSound = state.customSounds[soundIndex];
        if (removedSound.audioURL) {
          URL.revokeObjectURL(removedSound.audioURL);
        }
        state.customSounds.splice(soundIndex, 1);
      }
    },

    clearAllCustomSounds(state) {
      state.customSounds.forEach(sound => {
        if (sound.audioURL) {
          URL.revokeObjectURL(sound.audioURL);
        }
      });
      state.customSounds = [];
    },

    updateCustomSoundName(state, action) {
      const { soundId, newName } = action.payload;
      const sound = state.customSounds.find(s => s.id === soundId);
      if (sound) sound.name = newName;
    },

    setShowHumanDetection(state, action) {
      state.showHumanDetection = action.payload;
    },
    setCardModeEnabled(state, action) {
      state.cardModeEnabled = Boolean(action.payload);
    },
    setQrScanEnabled(state, action) {
      state.qrScanEnabled = Boolean(action.payload);
    },
    setNumberDetectEnabled(state, action) {
      state.numberDetectEnabled = Boolean(action.payload);
    },

    syncActorsWithFaces(state, action) {
      const faceCount = action.payload;
      const scene = state.scenes[state.currentSceneIndex];
      if (!scene) return;

      const currentActorCount = scene.actors.length;

      if (faceCount > currentActorCount) {
        for (let i = 0; i < faceCount - currentActorCount; i++) {
          scene.actors.push(makeDefaultstem());
        }
      } else if (faceCount < currentActorCount) {
        for (let i = 0; i < currentActorCount - faceCount && scene.actors.length > 1; i++) {
          scene.actors.pop();
        }
      }
    },

    setVideoOpacity(state, action) {
      state.videoOpacity = Math.min(Math.max(0, action.payload), 100);
    },

    // ===== Simulator robot management =====
    addSimulatorRobot(state, action) {
      const robot = action.payload || {};
      const simWidth = getSimGridWidth(state);
      const simHeight = getSimGridHeight(state);
      const randomInRange = (max) => {
        if (max <= 2) return 0;
        return Math.floor(Math.random() * (max - 2)) + 1;
      };
      state.simulatorRobots.push({
        id: robot.id || `simRobot-${Date.now()}-${Math.random()}`,
        name: robot.name || 'Robot',
        image: robot.image || './assets/characters/stembot.svg',
        x: Number.isFinite(robot.x) ? robot.x : randomInRange(simWidth),
        y: Number.isFinite(robot.y) ? robot.y : randomInRange(simHeight),
        direction: robot.direction || 0,
        size: robot.size || 1,
        scripts: robot.scripts ? deepClone(robot.scripts) : [],
        visible: true,
        type: 'simulatorRobot',
        speechSettings: robot.speechSettings || makeDefaultSpeechSettings(),
      });
    },

    removeSimulatorRobot(state, action) {
      const robotId = action.payload;
      state.simulatorRobots = state.simulatorRobots.filter(r => r.id !== robotId);
      if (state.selectedSimRobotId === robotId) state.selectedSimRobotId = null;
    },

    moveSimulatorRobot(state, action) {
      const { id, x, y, direction } = action.payload;
      const robot = state.simulatorRobots.find(r => r.id === id);
      if (robot) {
        const simWidth = getSimGridWidth(state);
        const simHeight = getSimGridHeight(state);

        robot.x = Math.max(0, Math.min(x, simWidth - 1));
        robot.y = Math.max(0, Math.min(y, simHeight - 1));
        if (direction !== undefined && direction !== null) {
          robot.direction = direction;
        }
      }
    },

    // movement from scripts (simulator)
    moveSimulatorRobotFromScript: (state, action) => {
      const { robotId, x, y, direction } = action.payload;
      const robotIndex = state.simulatorRobots.findIndex(robot => robot.id === robotId);

      if (robotIndex !== -1) {
        const simWidth = getSimGridWidth(state);
        const simHeight = getSimGridHeight(state);

        state.simulatorRobots[robotIndex].x = Math.max(0, Math.min(x, simWidth - 1));
        state.simulatorRobots[robotIndex].y = Math.max(0, Math.min(y, simHeight - 1));

        if (direction !== undefined) {
          state.simulatorRobots[robotIndex].direction = direction;
        }
      } else {
        console.error(`?? REDUX ERROR: Robot ${robotId} not found`);
      }
    },

    rotateSimulatorRobotFromScript(state, action) {
      const { robotId, degrees } = action.payload;
      const robot = state.simulatorRobots.find(r => r.id === robotId);
      if (robot) {
        let newDir = (robot.direction + degrees) % 360;
        if (newDir < 0) newDir += 360;
        robot.direction = newDir;
      }
    },

    scaleSimulatorRobotFromScript(state, action) {
      const { robotId, scale } = action.payload;
      const robot = state.simulatorRobots.find(r => r.id === robotId);
      if (robot) {
        const newSize = (robot.size || 1) * scale;
        robot.size = Math.min(Math.max(0.5, newSize), 4.0);
      }
    },

    disappearSimulatorRobot(state, action) {
      const { robotId } = action.payload;
      const robot = state.simulatorRobots.find(r => r.id === robotId);
      if (robot) {
        robot.visible = false;
      }
    },

    reappearSimulatorRobot(state, action) {
      const { robotId } = action.payload;
      const robot = state.simulatorRobots.find(r => r.id === robotId);
      if (robot) {
        robot.visible = true;
      }
    },

    // simulator script editors
    addBlockToSimulatorScript(state, action) {
      const { robotId, block, index } = action.payload;
      const robot = state.simulatorRobots.find(r => r.id === robotId);
      if (!robot) return;

      const referenceWidth = Math.max(1, Number.isFinite(block.layoutRefWidth) ? block.layoutRefWidth : DEFAULT_SCRIPTAREA_WIDTH);
      const referenceHeight = Math.max(1, Number.isFinite(block.layoutRefHeight) ? block.layoutRefHeight : DEFAULT_SCRIPTAREA_HEIGHT);
      const safeX = Math.max(0, Math.min(Number.isFinite(block.x) ? block.x : 50, referenceWidth));
      const safeY = Math.max(0, Math.min(Number.isFinite(block.y) ? block.y : 50, referenceHeight));
      const xRatio = Number.isFinite(block.xRatio) ? block.xRatio : safeX / referenceWidth;
      const yRatio = Number.isFinite(block.yRatio) ? block.yRatio : safeY / referenceHeight;

      const newBlock = {
        id: nanoid(),
        type: block.name,
        ...block,
        x: safeX,
        y: safeY,
        layoutRefWidth: referenceWidth,
        layoutRefHeight: referenceHeight,
        xRatio,
        yRatio,
        nextBlockId: null,  // ID of block connected to the right
        // Existing fields
        steps: block.category === 'move' ? block.steps ?? 10 : undefined,
        angle: block.category === 'turn' ? block.angle ?? 90 : undefined,
        duration: block.category === 'wait' ? block.duration ?? 1000 : undefined,
        soundData: block.soundData || null,
        audioURL: block.soundData?.audioURL || null,
        lowFrequency: block.type === 'obstaclesound' ? block.lowFrequency ?? 1 : undefined,
        highFrequency: block.type === 'obstaclesound' ? block.highFrequency ?? 99 : undefined,
        pathColor: block.pathColor || (block.type === 'setPathColor' ? '#000000' : undefined),
        maxSteps: block.maxSteps || (block.type === 'followPath' ? 50 : undefined),
      };


      const desired = Number.isInteger(index) ? index : robot.scripts.length;
      const clampedIndex = Math.max(0, Math.min(desired, robot.scripts.length));
      robot.scripts.splice(clampedIndex, 0, newBlock);

      const t = resolveTarget(state, { robotId });
      if (t) syncHistoryPresentFor(state, t);
    },

    clearSimulatorScript(state, action) {
      const robotId = action.payload.robotId;
      const robot = state.simulatorRobots.find(r => r.id === robotId);
      if (!robot) return;

      const t = { type: 'simRobot', id: robot.id, scripts: robot.scripts || [] };
      const key = `simRobot:${robot.id}`;
      const bucket = ensureBucket(state, key);

      bucket.past.push(cloneScripts(robot.scripts));
      bucket.future = [];
      robot.scripts = [];
      bucket.present = [];
    },

    updateSimulatorBlockColor(state, action) {
      const { robotId, blockId, color, property = 'pathColor' } = action.payload;
      const robot = state.simulatorRobots.find(r => r.id === robotId);
      if (!robot) return;

      const block = robot.scripts.find(b => b.id === blockId);
      if (block) {
        if (property === 'messageColor') {
          const normalized = normalizeMessageColor(color || DEFAULT_MESSAGE_COLOR);
          block.messageColor = normalized;
          block.icon = getMessageIconPath(block.type, normalized) || block.icon;
        } else {
          block[property] = color;
        }
      }

      const t = resolveTarget(state, { robotId });
      if (t) syncHistoryPresentFor(state, t);
    },

    updateSimulatorBlockCount(state, action) {
      const { robotId, blockId, newCount, property = 'count' } = action.payload;
      const robot = state.simulatorRobots.find(r => r.id === robotId);
      if (!robot) return;

      const block = robot.scripts.find(b => b.id === blockId);
      if (block) {
        if (property === 'pinConfig' || property === 'part' || property === 'expression') {
          block[property] = action.payload.value ?? block[property];
        } else if (property === 'opacity') {
          block[property] = Math.max(0, Math.min(100, newCount));
        } else {
          block[property] = Math.max(0, newCount);
        }
      }

      const t = resolveTarget(state, { robotId });
      if (t) syncHistoryPresentFor(state, t);
    },

    updateSimulatorBlockMessage(state, action) {
      const { robotId, blockId, message } = action.payload;
      const robot = state.simulatorRobots.find(r => r.id === robotId);
      if (!robot) return;

      const block = robot.scripts.find(b => b.id === blockId);
      if (block) {
        block.message = message;
      }

      const t = resolveTarget(state, { robotId });
      if (t) syncHistoryPresentFor(state, t);
    },

    // cycle robot type
    cycleSimulatorRobot(state) {
      const library = [
        { name: 'Stembot', image: './assets/characters/stembot.svg' },
        { name: 'EdisonV3 Car', image: './assets/characters/edisonv3.jpg' },
        { name: 'Robot Dog', image: './assets/characters/calliope2016.jpg' },
        { name: 'Robot Car', image: './assets/characters/robotino.jpg' },
      ];

      const prevRobot = state.simulatorRobots[0];
      const simWidth = getSimGridWidth(state);
      const simHeight = getSimGridHeight(state);
      const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
      const randomX = Math.floor(Math.random() * simWidth);
      const randomY = Math.floor(Math.random() * simHeight);
      const prevX = clamp(prevRobot?.x ?? randomX, 0, simWidth - 1);
      const prevY = clamp(prevRobot?.y ?? randomY, 0, simHeight - 1);
      const prevDir = prevRobot?.direction ?? 0;
      const prevSize = prevRobot?.size ?? 1;

      state.simulatorRobots = [];

      state.currentSimulatorRobotIndex =
        (state.currentSimulatorRobotIndex + 1) % library.length;

      const next = library[state.currentSimulatorRobotIndex];

      state.simulatorRobots.push({
        id: `simRobot-${Date.now()}-${Math.random()}`,
        name: next.name,
        image: next.image,
        x: prevX,
        y: prevY,
        direction: prevDir,
        size: prevSize,
        scripts: [],
        visible: true,
        type: 'simulatorRobot',
        speechSettings: makeDefaultSpeechSettings(),
      });

      state.selectedSimRobotId = state.simulatorRobots[0]?.id ?? null;
    },

    setSelectedSimRobot(state, action) {
      state.selectedSimRobotId = action.payload ?? null;
    },
    setSimulatorPath(state, action) {
      state.simulatorPath = action.payload || null;
    },
    setDrawLineSettings(state, action) {
      const next = { ...state.drawLineSettings, ...action.payload };
      state.drawLineSettings = next;
      if (!next.enabled) {
        state.scenes.forEach(clearSceneTrails);
      }
    },
    clearActorTrails(state) {
      const scene = state.scenes[state.currentSceneIndex];
      if (scene) {
        clearSceneTrails(scene);
      }
    },

    overwrite(state, action) {
      const newState = action.payload;
      state.scenes = newState.scenes || [makeDefaultScene()];
      state.scenes.forEach(scene => {
        if (!scene.actorTrails) {
          scene.actorTrails = {};
        }
        if (Array.isArray(scene.actors)) {
          scene.actors.forEach(ensureActorSpeechSettings);
        }
      });
      state.currentSceneIndex = newState.currentSceneIndex || 0;
      state.sceneUndoStack = newState.sceneUndoStack || [];
      state.sceneRedoStack = newState.sceneRedoStack || [];
      state.selectedBlockCategory = newState.selectedBlockCategory || 'motion';
      state.categoryPanelOpen = newState.categoryPanelOpen || false;
      state.sounds = newState.sounds || { pop: './assets/sounds/pop.mp3' };
      state.backgroundGallery = newState.backgroundGallery || [
        './assets/backgrounds/bg1.png',
        './assets/backgrounds/bg2.png',
        './assets/backgrounds/bg3.png',
        '#ffffff', '#87CEEB', '#98FB98', '#FFB6C1', '#F0E68C'
      ];
      state.customSounds = newState.customSounds || [];
      state.videoOpacity = newState.videoOpacity !== undefined ? newState.videoOpacity : 100;
      state.cardModeEnabled = newState.cardModeEnabled ?? false;

      state.simulatorRobots = newState.simulatorRobots || [];
      state.simulatorRobots.forEach(ensureActorSpeechSettings);
      state.currentSimulatorRobotIndex = newState.currentSimulatorRobotIndex || 0;
      state.selectedSimRobotId = newState.selectedSimRobotId ?? null;
      state.simulatorOpen = newState.simulatorOpen ?? false;
      state.aiChatOpen = newState.aiChatOpen ?? false;
      state.drawLineSettings = newState.drawLineSettings || { ...DEFAULT_DRAW_LINE_SETTINGS };
      state.simGridWidth = Math.max(1, Math.floor(newState.simGridWidth ?? SIM_DEFAULT_GRID_WIDTH));
      state.simGridHeight = Math.max(1, Math.floor(newState.simGridHeight ?? SIM_DEFAULT_GRID_HEIGHT));

      // reset script history on overwrite
      state.history = { byTarget: {} };
    },
    // ?? NEW: Update block position (for dragging blocks in 2D)
    updateBlockPosition(state, action) {
      const { actorId, robotId, blockId, x, y, viewportWidth, viewportHeight } = action.payload;
      const scene = state.scenes[state.currentSceneIndex];

      let targetScripts;
      if (robotId) {
        const robot = state.simulatorRobots.find(r => r.id === robotId);
        if (!robot) return;
        targetScripts = robot.scripts;
      } else if (actorId) {
        const actor = scene?.actors?.find(a => a.id === actorId);
        if (!actor) return;
        targetScripts = actor.scripts;
      } else {
        return;
      }

      const block = targetScripts.find(b => b.id === blockId);
      if (block) {
        const width = Math.max(
          1,
          Number.isFinite(viewportWidth)
            ? viewportWidth
            : Number.isFinite(block?.layoutRefWidth)
              ? block.layoutRefWidth
              : DEFAULT_SCRIPTAREA_WIDTH
        );
        const height = Math.max(
          1,
          Number.isFinite(viewportHeight)
            ? viewportHeight
            : Number.isFinite(block?.layoutRefHeight)
              ? block.layoutRefHeight
              : DEFAULT_SCRIPTAREA_HEIGHT
        );
        const safeX = Math.max(0, Math.min(Number.isFinite(x) ? x : block.x ?? 0, width));
        const safeY = Math.max(0, Math.min(Number.isFinite(y) ? y : block.y ?? 0, height));

        block.x = safeX;
        block.y = safeY;
        block.layoutRefWidth = width;
        block.layoutRefHeight = height;
        block.xRatio = width ? safeX / width : 0;
        block.yRatio = height ? safeY / height : 0;
      }
    },

    // ?? NEW: Connect two blocks (snap together horizontally)
    connectBlocks(state, action) {
      const { actorId, robotId, leftBlockId, rightBlockId } = action.payload;
      const scene = state.scenes[state.currentSceneIndex];

      let targetScripts;
      if (robotId) {
        const robot = state.simulatorRobots.find(r => r.id === robotId);
        if (!robot) return;
        targetScripts = robot.scripts;
      } else if (actorId) {
        const actor = scene?.actors?.find(a => a.id === actorId);
        if (!actor) return;
        targetScripts = actor.scripts;
      } else {
        return;
      }

      const leftBlock = targetScripts.find(b => b.id === leftBlockId);
      const rightBlock = targetScripts.find(b => b.id === rightBlockId);

      if (leftBlock) {
        leftBlock.nextBlockId = rightBlockId;
      }

      // ?? NEW: Clear the x,y of the right block so it calculates from chain
      if (rightBlock) {
        rightBlock.x = null;
        rightBlock.y = null;
      }
    },

    // ?? NEW: Disconnect blocks
    disconnectBlock(state, action) {
      const { actorId, robotId, blockId } = action.payload;
      const scene = state.scenes[state.currentSceneIndex];

      let targetScripts;
      if (robotId) {
        const robot = state.simulatorRobots.find(r => r.id === robotId);
        if (!robot) return;
        targetScripts = robot.scripts;
      } else if (actorId) {
        const actor = scene?.actors?.find(a => a.id === actorId);
        if (!actor) return;
        targetScripts = actor.scripts;
      } else {
        return;
      }

      const block = targetScripts.find(b => b.id === blockId);
      if (block) {
        block.nextBlockId = null;
      }
    },

  },
});

export const {
  // scene-level undo
  undoSceneAction,
  redoSceneAction,

  // ui
  setSelectedBlockCategory,
  toggleCategoryPanel,
  setStageGridSize,
  setSimulatorGridSize,
  setSceneHeading,

  // script-area and scene-level snapshot
  pushUndoState,

  // script-only undo/redo
  undo,
  redo,

  // per-scene undo
  undoLastAction,
  redoLastAction,
  pushSceneUndoState,

  // sprite messages
  showSpriteMessage,
  hideSpriteMessage,
  // scene crud
  addScene,
  removeScene,
  switchScene,

  // actors
  scaleActor,
  resetActorSize,
  addActor,
  removeActor,
  disappearActor,
  reappearActor,
  moveActor,
  setActorPosition,
  rotateActor,
  setActorLanguage,

  // background
  setBackground,
  addBackgroundToGallery,
  removeBackgroundFromGallery,
  cycleNextBackground,

  // scripts (stage)
  addBlockToScript,
  clearScript,
  updateBlockCount,
  updateBlockPosition,
  connectBlocks,
  disconnectBlock,
  updateBlockColor,
  updateBlockMessage,
  updateBlockCustomData,
  updateObstacleFrequency,
  addBlockToContainer,
  removeBlockFromContainer,
  moveBlockBetweenContainers,

  // sound library
  addCustomSound,
  removeCustomSound,
  clearAllCustomSounds,
  updateCustomSoundName,

  // misc
  setShowHumanDetection,
  setVideoOpacity,
  setCardModeEnabled,
  setQrScanEnabled,
  setNumberDetectEnabled,
  syncActorsWithFaces,
  overwrite,
  setCameraState,

  // obstacles & colored areas
  addObstacle,
  removeObstacle,
  moveObstacle,
  addColoredArea,
  removeColoredArea,
  moveColoredArea,

  // simulator
  addSimulatorRobot,
  removeSimulatorRobot,
  moveSimulatorRobot,
  cycleSimulatorRobot,
  moveSimulatorRobotFromScript,
  rotateSimulatorRobotFromScript,
  scaleSimulatorRobotFromScript,
  disappearSimulatorRobot,
  reappearSimulatorRobot,
  addBlockToSimulatorScript,
  clearSimulatorScript,
  updateSimulatorBlockColor,
  updateSimulatorBlockCount,
  updateSimulatorBlockMessage,
  setSelectedSimRobot,
  setSimulatorPath,
  setSimulatorOpen,
  setRobotModeOpen,
  setAiChatOpen,
  updateActorCustomData,
  setRobotState,
  setDrawLineSettings,
  clearActorTrails,

  // device
  setSelectedDevice,
} = sceneSlice.actions;

export default sceneSlice.reducer;

/* ================= Selectors for toolbar enable/disable ================= */
// Generic selectors that work with either an actor or a robot:
export const selectCanUndo = (state, { actorId, robotId }) => {
  const s = state.scene;
  const key = robotId ? `simRobot:${robotId}` : actorId ? `actor:${actorId}` : null;
  if (!key) return false;
  const b = s.history.byTarget[key];
  return !!b && (b.past?.length ?? 0) > 0;
};

export const selectCanRedo = (state, { actorId, robotId }) => {
  const s = state.scene;
  const key = robotId ? `simRobot:${robotId}` : actorId ? `actor:${actorId}` : null;
  if (!key) return false;
  const b = s.history.byTarget[key];
  return !!b && (b.future?.length ?? 0) > 0;
};

// Convenience: for current selected simulator robot (if your Toolbar wants it)
export const selectCanUndoCurrent = (state) => {
  const s = state.scene;
  const id = s.selectedSimRobotId;
  if (!id) return false;
  const b = s.history.byTarget[`simRobot:${id}`];
  return !!b && (b.past?.length ?? 0) > 0;
};
export const selectCanRedoCurrent = (state) => {
  const s = state.scene;
  const id = s.selectedSimRobotId;
  if (!id) return false;
  const b = s.history.byTarget[`simRobot:${id}`];
  return !!b && (b.future?.length ?? 0) > 0;
};

/* ================= Open Roberta helpers (unchanged) ================= */
// Coordinate normalization helpers (grid cells <-> unit [0..1])
const __toUnit__ = (v, max) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n / max));
};

const __fromUnit__ = (v, max) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.round(Math.max(0, Math.min(1, n)) * max);
};

// Build Open Roberta Lab simulation configuration
export function toOpenRobertaSimConfig(sceneSliceState) {
  try {
    const currentScene = sceneSliceState?.scenes?.[sceneSliceState?.currentSceneIndex] || {};
    const robots = sceneSliceState?.simulatorRobots || [];
    const { width: gridW, height: gridH } = getStageGridDimensions(sceneSliceState || {});

    const robotPosesInner = robots.map(r => ({
      x: __toUnit__(r?.x ?? 0, gridW),
      y: __toUnit__(r?.y ?? 0, gridH),
      theta: Number.isFinite(r?.direction) ? r.direction : 0
    }));

    const obstacles = (currentScene?.obstacles || []).map(obs => {
      const wCells = Number.isFinite(obs?.w) ? obs.w
        : (Number.isFinite(obs?.width) ? obs.width : 1);
      const hCells = Number.isFinite(obs?.h) ? obs.h
        : (Number.isFinite(obs?.height) ? obs.height : 1);

      return {
        x: __toUnit__(obs?.x ?? 0, gridW),
        y: __toUnit__(obs?.y ?? 0, gridH),
        w: __toUnit__(wCells, gridW),
        h: __toUnit__(hCells, gridH),
        theta: Number.isFinite(obs?.theta) ? obs.theta : (Number.isFinite(obs?.rotation) ? obs.rotation : 0),
        color: obs?.color || '#33B8CA',
        form: obs?.form || obs?.shape || 'RECTANGLE',
        type: obs?.type || 'OBSTACLE'
      };
    });

    const colorAreas = (currentScene?.coloredAreas || []).map(area => {
      const wCells = Number.isFinite(area?.w) ? area.w
        : (Number.isFinite(area?.width) ? area.width : 1);
      const hCells = Number.isFinite(area?.h) ? area.h
        : (Number.isFinite(area?.height) ? area.height : 1);
      return {
        x: __toUnit__(area?.x ?? 0, gridW),
        y: __toUnit__(area?.y ?? 0, gridH),
        w: __toUnit__(wCells, gridW),
        h: __toUnit__(hCells, gridH),
        color: area?.color || '#000000',
        type: area?.type || 'COLOR_AREA'
      };
    });

    const marker = [];

    return {
      robotPoses: [robotPosesInner],
      obstacles,
      colorAreas,
      marker
    };
  } catch (e) {
    console.error('toOpenRobertaSimConfig error:', e);
    return { robotPoses: [[]], obstacles: [], colorAreas: [], marker: [] };
  }
}

/**
 * Thunk: Apply an Open Roberta-style config back into current scene.
 */
export const applyOpenRobertaSimConfigThunk = (openRobertaConfig) => (dispatch, getState) => {
  try {
    if (!openRobertaConfig || typeof openRobertaConfig !== 'object') return;

    const state = getState();
    const sceneState = state?.scene;
    if (!sceneState) return;

    const { width: gridW, height: gridH } = getStageGridDimensions(sceneState);

    // 1) Obstacles
    const obs = Array.isArray(openRobertaConfig.obstacles) ? openRobertaConfig.obstacles : [];
    for (const o of obs) {
      const cellX = __fromUnit__(o?.x ?? 0, gridW);
      const cellY = __fromUnit__(o?.y ?? 0, gridH);
      const cellW = Math.max(1, __fromUnit__(o?.w ?? 0, gridW));
      const cellH = Math.max(1, __fromUnit__(o?.h ?? 0, gridH));

      dispatch(addObstacle({
        id: `orl-${Date.now()}-${Math.random()}`,
        x: cellX,
        y: cellY,
        w: cellW,
        h: cellH,
        theta: Number.isFinite(o?.theta) ? o.theta : 0,
        rotation: Number.isFinite(o?.theta) ? o.theta : 0,
        color: o?.color || '#33B8CA',
        shape: o?.form || 'RECTANGLE',
        form: o?.form || 'RECTANGLE',
        type: o?.type || 'OBSTACLE'
      }));
    }

    // 2) Color areas (optional)
    const areas = Array.isArray(openRobertaConfig.colorAreas) ? openRobertaConfig.colorAreas : [];
    for (const a of areas) {
      const cellX = __fromUnit__(a?.x ?? 0, gridW);
      const cellY = __fromUnit__(a?.y ?? 0, gridH);
      const cellW = Math.max(1, __fromUnit__(a?.w ?? 0, gridW));
      const cellH = Math.max(1, __fromUnit__(a?.h ?? 0, gridH));

      dispatch(addColoredArea({
        id: `orl-color-${Date.now()}-${Math.random()}`,
        x: cellX,
        y: cellY,
        width: cellW,
        height: cellH,
        color: a?.color || '#000000',
        type: 'coloredArea',
        blocking: false
      }));
    }

    // 3) Robot pose (first only)
    const firstPose = Array.isArray(openRobertaConfig.robotPoses)
      && Array.isArray(openRobertaConfig.robotPoses[0])
      && openRobertaConfig.robotPoses[0][0]
      ? openRobertaConfig.robotPoses[0][0]
      : null;

    if (firstPose) {
      const posX = __fromUnit__(firstPose.x ?? 0, gridW);
      const posY = __fromUnit__(firstPose.y ?? 0, gridH);
      const theta = Number.isFinite(firstPose.theta) ? firstPose.theta : 0;

      const currentRobots = sceneState.simulatorRobots || [];
      if (currentRobots.length === 0) {
        dispatch(addSimulatorRobot({
          name: 'Robot',
          x: posX,
          y: posY,
          direction: theta,
          image: './assets/characters/stembot.svg'
        }));
      } else {
        dispatch(moveSimulatorRobot({
          id: currentRobots[0].id,
          x: posX,
          y: posY,
          direction: theta
        }));
      }
    }
  } catch (e) {
    console.error('applyOpenRobertaSimConfigThunk error:', e);
  }
};









